/**
 * Isola falhas de um módulo (painel/diálogo): o resto do portal continua
 * funcionando. Com `dialog`, a mensagem de erro aparece dentro de um diálogo
 * (não deforma o layout).
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Dialog } from './primitives';

interface Props {
  name: string;
  children: ReactNode;
  /** Mostrar o erro num diálogo (para módulos que são diálogos). */
  dialog?: { title: string; open: boolean; onClose: () => void };
}

interface State {
  error: Error | null;
}

export class ModuleBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(`[AmaView] Falha no módulo "${this.props.name}":`, error, info.componentStack);
  }

  private retry = () => this.setState({ error: null });

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;
    const body = (
      <div className="flex flex-col items-center gap-3 px-6 py-8 text-center" role="alert">
        <AlertTriangle className="size-7 text-yellow-300" aria-hidden />
        <p className="text-sm text-ink-100">Esta parte ({this.props.name}) não carregou.</p>
        <button
          type="button"
          className="rounded-lg border border-ink-600 bg-ink-800 px-3 py-1.5 text-xs text-ink-100 hover:bg-ink-700"
          onClick={this.retry}
        >
          Tentar de novo
        </button>
      </div>
    );
    const { dialog } = this.props;
    if (!dialog) return body;
    return (
      <Dialog
        open={dialog.open}
        onClose={() => {
          this.retry();
          dialog.onClose();
        }}
        title={dialog.title}
      >
        {body}
      </Dialog>
    );
  }
}
